import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { inboxKeys } from "./queryKeys";
import { refreshParticipantPicture } from "./api";

/**
 * When an avatar URL has expired (LinkedIn CDN links go stale), ask the
 * backend for a fresh one and refetch the affected participant data.
 * Each participant is attempted once per session to avoid retry loops.
 */
export function useRefreshParticipantPicture() {
	const queryClient = useQueryClient();
	const attemptedRef = useRef(new Set<string>());

	return useCallback(
		async (participantId: string, conversationId?: string) => {
			if (!participantId || attemptedRef.current.has(participantId)) return;
			attemptedRef.current.add(participantId);
			try {
				const pictureUrl = await refreshParticipantPicture(participantId);
				if (pictureUrl) {
					queryClient.invalidateQueries({ queryKey: inboxKeys.participantDetails(participantId) });
					if (conversationId) {
						queryClient.invalidateQueries({ queryKey: inboxKeys.participants(conversationId) });
					}
				}
			} catch {
				// Picture stays on the fallback icon.
			}
		},
		[queryClient]
	);
}
